import React, { useMemo } from 'react';
import { ReceiptText } from 'lucide-react';

/* Colour cycle for the package share donut / ledger rows (brand blue first) */
const BREAKDOWN_COLORS = [
  '#375AE7',
  '#7C3AED',
  '#0EA5E9',
  '#10B981',
  '#F59E0B',
  '#F43F5E',
  '#14B8A6',
  '#8B5CF6',
];

/**
 * Usage breakdown — presentational panel only (no fetching, no side effects).
 *
 * Reads the exact same `spend` payload as before and renders it as:
 *   1. a "share of usage" donut (center = verification count) + legend
 *   2. a package ledger (name, count, unit price, amount, % share)
 *   3. a GST & totals receipt (Ex GST + GST rows, emphasised total)
 *
 * Replaces the old progress-bar cards. Drop-in usage:
 *   <UsageBreakdownPanel spend={spend} formatCents={formatCents} />
 */
const UsageBreakdownPanel = ({ spend, formatCents }) => {
  const rows = useMemo(() => {
    const packages = spend?.packages || [];
    const usageCents = spend?.usage_cents ?? 0;
    return packages.map((pkg, i) => ({
      ...pkg,
      color: BREAKDOWN_COLORS[i % BREAKDOWN_COLORS.length],
      share: usageCents > 0 ? (pkg.spend_cents / usageCents) * 100 : 0,
    }));
  }, [spend]);

  const usageCents = spend?.usage_cents ?? 0;
  const verificationCount = spend?.verification_count ?? 0;

  /* Donut geometry */
  const SIZE = 176;
  const STROKE = 20;
  const R = (SIZE - STROKE) / 2;
  const CIRC = 2 * Math.PI * R;

  let acc = 0;
  const segments = rows.map((r) => {
    const arc = usageCents > 0 ? (r.spend_cents / usageCents) * CIRC : 0;
    const seg = { ...r, arc, offset: acc };
    acc += arc;
    return seg;
  });
  const hasSegments = segments.some((s) => s.arc > 0);

  return (
    <div className="space-y-4 sm:space-y-5">
      {/* ── By package — donut + ledger ─────────────────────────── */}
      <div className="grid grid-cols-1 gap-4 lg:grid-cols-5">
        {/* Share of usage — donut */}
        <div className="rounded-3xl border border-slate-200 bg-gradient-to-br from-slate-50/80 via-white to-[#375AE7]/[0.06] p-4 sm:p-5">
          <p className="text-[10px] font-semibold uppercase tracking-[0.22em] text-slate-400">
            Share of usage
          </p>
          <div className="mt-2 flex items-center justify-center">
            <div className="relative">
              <svg
                viewBox={`0 0 ${SIZE} ${SIZE}`}
                className="h-40 w-40 -rotate-90 sm:h-44 sm:w-44"
                role="img"
                aria-label="Usage share by package"
              >
                <circle
                  cx={SIZE / 2}
                  cy={SIZE / 2}
                  r={R}
                  fill="none"
                  stroke="#EEF2F7"
                  strokeWidth={STROKE}
                />
                {hasSegments &&
                  segments.map((s) =>
                    s.arc > 0 ? (
                      <circle
                        key={s.code ?? s.name}
                        cx={SIZE / 2}
                        cy={SIZE / 2}
                        r={R}
                        fill="none"
                        stroke={s.color}
                        strokeWidth={STROKE}
                        strokeLinecap="butt"
                        strokeDasharray={`${Math.max(s.arc - 2, 0)} ${
                          CIRC - Math.max(s.arc - 2, 0)
                        }`}
                        strokeDashoffset={-s.offset}
                      />
                    ) : null,
                  )}
              </svg>
              <div className="absolute inset-0 flex flex-col items-center justify-center">
                <p className="text-2xl font-bold tabular-nums text-slate-900">
                  {verificationCount}
                </p>
                <p className="text-[10px] font-medium uppercase tracking-wider text-slate-400">
                  verifications
                </p>
              </div>
            </div>
          </div>

          {/* Legend */}
          <div className="mt-4 space-y-1.5">
            {rows.map((r) => (
              <div
                key={r.code ?? r.name}
                className="flex items-center gap-2 text-xs"
              >
                <span
                  className="h-2 w-2 shrink-0 rounded-full"
                  style={{ backgroundColor: r.color }}
                />
                <span className="min-w-0 flex-1 truncate capitalize text-slate-600">
                  {r.name || r.code}
                </span>
                <span className="shrink-0 font-semibold tabular-nums text-slate-800">
                  {r.share.toFixed(1)}%
                </span>
              </div>
            ))}
            {rows.length === 0 && (
              <p className="py-2 text-center text-xs text-slate-400">
                No package usage recorded.
              </p>
            )}
          </div>
        </div>

        {/* Package ledger */}
        <div className="rounded-3xl border border-slate-200 bg-white p-4 sm:p-5 lg:col-span-4">
          <div className="flex flex-wrap items-center justify-between gap-2">
            <div>
              <h4 className="text-sm font-semibold text-slate-900">
                Usage by package
              </h4>
              <p className="mt-0.5 text-xs text-slate-500">
                Breakdown of verifications for the selected period
              </p>
            </div>
            <span className="rounded-full bg-slate-100 px-2.5 py-1 text-[10px] font-semibold text-slate-500">
              {rows.length} package{rows.length === 1 ? '' : 's'}
            </span>
          </div>

          <div className="mt-3 space-y-2.5">
            {rows.length === 0 ? (
              <div className="rounded-2xl border border-dashed border-slate-200 px-4 py-8 text-center text-sm text-slate-400">
                No package usage recorded.
              </div>
            ) : (
              rows.map((r) => (
                <div
                  key={r.code ?? r.name}
                  className="flex items-center gap-3 rounded-2xl border border-slate-200 bg-white p-3 transition-all duration-200 hover:border-[#375AE7]/30 hover:shadow-[0_10px_28px_-18px_rgba(55,90,231,0.5)] sm:p-3.5"
                >
                  <span
                    className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl text-sm font-bold"
                    style={{
                      backgroundColor: `${r.color}14`,
                      color: r.color,
                    }}
                  >
                    {(r.name || r.code || '?').charAt(0).toUpperCase()}
                  </span>
                  <div className="min-w-0 flex-1">
                    <p className="truncate text-sm font-semibold capitalize text-slate-800">
                      {r.name || r.code}
                    </p>
                    <p className="mt-0.5 truncate text-xs text-slate-500">
                      {r.count ?? 0} verification
                      {(r.count ?? 0) === 1 ? '' : 's'} ·{' '}
                      {formatCents(r.unit_price_cents)} / scan
                    </p>
                  </div>
                  <div className="shrink-0 text-right">
                    <p className="text-sm font-bold tabular-nums text-slate-900">
                      {formatCents(r.spend_cents)}
                    </p>
                    <p
                      className="mt-0.5 text-[11px] font-semibold tabular-nums"
                      style={{ color: r.color }}
                    >
                      {r.share.toFixed(1)}% of usage
                    </p>
                  </div>
                </div>
              ))
            )}
          </div>
        </div>
      </div>

      {/* ── GST & totals — receipt ──────────────────────────────── */}
      <div className="rounded-3xl border border-slate-200 bg-white p-4 sm:p-5">
        <div className="flex flex-wrap items-center justify-between gap-2">
          <div className="flex items-center gap-2.5">
            <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-xl bg-[#375AE7]/10 text-[#375AE7]">
              <ReceiptText size={16} />
            </span>
            <div>
              <h4 className="text-sm font-semibold text-slate-900">
                GST &amp; totals
              </h4>
              <p className="text-xs text-slate-500">
                Ex GST, GST and total charged for this period
              </p>
            </div>
          </div>
          <span className="rounded-full bg-slate-100 px-2.5 py-1 text-[10px] font-semibold tabular-nums text-slate-500">
            {verificationCount} verification
            {verificationCount === 1 ? '' : 's'}
          </span>
        </div>

        <div className="mt-4 grid gap-4 lg:grid-cols-3">
          {/* Receipt rows */}
          <div className="space-y-3 rounded-2xl border border-slate-100 bg-slate-50/60 p-4 lg:col-span-2">
            <div className="flex items-center justify-between text-sm">
              <span className="text-slate-500">Subtotal (Ex GST)</span>
              <span className="font-semibold tabular-nums text-slate-800">
                {formatCents(spend?.ex_gst_cents)}
              </span>
            </div>
            <div className="border-t border-dashed border-slate-200" />
            <div className="flex items-center justify-between text-sm">
              <span className="text-slate-500">GST</span>
              <span className="font-semibold tabular-nums text-slate-800">
                {formatCents(spend?.tax_cents)}
              </span>
            </div>
            <div className="border-t border-dashed border-slate-200" />
            <p className="text-[11px] leading-relaxed text-slate-400">
              Totals for the selected period. Credit applied is shown in the
              summary cards above.
            </p>
          </div>

          {/* Emphasised total */}
          <div className="flex flex-col justify-center rounded-2xl border border-emerald-200/70 bg-gradient-to-br from-emerald-500/10 to-teal-500/10 p-4">
            <p className="text-[10px] font-semibold uppercase tracking-[0.18em] text-emerald-600">
              Total charged
            </p>
            <p className="mt-1 text-2xl font-bold tabular-nums text-emerald-700">
              {formatCents(spend?.total_cents)}
            </p>
            <p className="mt-0.5 text-xs text-emerald-600/80">
              Amount charged (incl GST)
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default UsageBreakdownPanel;
